/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Card,
  DetailStack, Message, Text,
} from '@doveit/bricks';
import { CardHeaderProps } from '@doveit/bricks/dist/typings/components/card/composition';
import { Agency } from '../../../providers/api/dtos';
import * as styles from './ViewAgencyPreview.style';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import useAgency from '../../hooks/use-agency/useAgency';
import { AgencyAvatar } from '../../components';
import { calculateMembershipStatus } from '../../../agent/utils/calculateMembershipStatus';
import { mapToAgencyPreviewStatus } from '../../components/constants';
import ViewEntityPreviewSkeleton from '../../../components/view-entity-preview-skeleton/ViewEntityPreviewSkeleton';
import { NO_VALUE_SYMBOL } from '../../../property/utils';

interface ViewAgencyPreviewBaseProps extends React.AriaAttributes, Pick<CardHeaderProps, 'title' | 'primaryActions' | 'secondaryActions'> {
  onClick?: (agency: Agency) => void,
}

export type ViewAgencyPreviewProps = ViewAgencyPreviewBaseProps & ({
  agencyId: NonNullable<Agency['id']>,
  agency?: undefined,
} | {
  agency: Agency,
  agencyId?: undefined,
});

export const LOAD_AGENCY_DATA_ERROR_MESSAGE = 'Non è stato possibile caricare i dati dell\'agenzia';

const ViewAgencyPreview: React.FC<ViewAgencyPreviewProps> = ({
  agencyId,
  title = 'Agenzia',
  primaryActions,
  secondaryActions,
  agency: externalAgency,
  onClick,
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');
  const { data: loadedAgency, isLoading: isAgencyLoading, error: agencyError } = useAgency(agencyId);
  const agency = externalAgency || loadedAgency;
  const agencyStatus = React.useMemo(() => {
    if (agency) {
      return calculateMembershipStatus(
        new Date(agency.startDate),
        agency.endDate ? new Date(agency.endDate) : undefined,
      );
    }

    return undefined;
  }, [agency]);

  if (isAgencyLoading && !agencyError) {
    return <ViewEntityPreviewSkeleton aria-label="Informazioni agenzia in caricamento" />;
  }

  if (agencyError) {
    return (
      <Message
        type="critical"
        message={LOAD_AGENCY_DATA_ERROR_MESSAGE}
      />
    );
  }

  return agency && (
    <Card
      aria-label="Anteprima agenzia"
      onClick={onClick ? () => onClick(agency) : undefined}
      {...rest}
    >
      {(primaryActions || secondaryActions) && (
        <Card.Header
          aria-label="Anteprima agenzia (header)"
          title={title}
          primaryActions={primaryActions}
          secondaryActions={secondaryActions}
        />
      )}
      <Card.Content>
        <div css={styles.basic}>
          <div css={styles.info}>
            <AgencyAvatar
              size={isMobile ? 'M' : 'L'}
              status={agencyStatus && mapToAgencyPreviewStatus[agencyStatus]}
              franchising={agency.franchising}
              photoId={agency.photoId}
            />
            <div css={styles.textInfo}>
              <Text.H4
                aria-label="Anteprima agenzia (title)"
                lineHeight="1"
                as="div"
              >
                {agency.shortName || agency.name}
              </Text.H4>
              {agency.phoneNumber && (
                <Text.Body
                  lineHeight="1"
                  as="div"
                >
                  {agency.phoneNumber}
                </Text.Body>
              )}
            </div>
          </div>
          {isMobile && (<Card.Divider />)}
          <div css={styles.detailsWrapper}>
            <div css={styles.details}>
              <div>
                <DetailStack
                  label="Indirizzo"
                  aria-label={'Anteprima dell\'agenzia (indirizzo)'}
                  size="M"
                >
                  {agency.address?.normalizedAddress || NO_VALUE_SYMBOL}
                </DetailStack>
              </div>
            </div>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

export default ViewAgencyPreview;
